import constants from '#packages/constants';
import {
  adjustLayoutToFit,
  adjustLayoutToFitSection,
  areAllComponentsMobileOnly,
} from '../utils/layoutUtils';
import type { CompLayout } from '@wix/document-services-types';
import type {
  AdjustLayoutOptions,
  LayoutAdjustmentStrategy,
  PluginScope,
} from './types';
import type { StageRect } from '#packages/stateManagement';

const { COMP_TYPES } = constants;

class DefaultLayoutAdjustmentStrategy implements LayoutAdjustmentStrategy {
  private containerRect: StageRect;

  initialize(scope: PluginScope): void {
    this.containerRect = scope.parentContainers[0].rect;
  }

  finalize(_scope: PluginScope): void {
    this.containerRect = null;
  }

  adjustLayout(
    _scope: PluginScope,
    layout: CompLayout,
    options: AdjustLayoutOptions,
  ): CompLayout {
    return adjustLayoutToFit(this.containerRect, layout, options.onCollision);
  }
}

class SectionLayoutAdjustmentStrategy implements LayoutAdjustmentStrategy {
  private containerRect: StageRect;

  initialize(scope: PluginScope): void {
    this.containerRect = scope.parentContainers[0].rect;
  }

  finalize(_scope: PluginScope): void {
    this.containerRect = null;
  }

  adjustLayout(
    _scope: PluginScope,
    layout: CompLayout,
    options: AdjustLayoutOptions,
  ): CompLayout {
    return adjustLayoutToFitSection(
      this.containerRect,
      layout,
      options.onCollision,
    );
  }
}

class MultiselectLayoutAdjustmentStrategy implements LayoutAdjustmentStrategy {
  private containerRect: StageRect;

  initialize(scope: PluginScope): void {
    const { editorAPI, selectedComp } = scope;

    const layoutGet =
      editorAPI.components.layout.measure.getBoundingLayoutRelativeToScreen;

    const layout = layoutGet(selectedComp);

    const minContainerRect = {
      top: Infinity,
      bottom: Infinity,
      left: Infinity,
      right: Infinity,
    };

    selectedComp.forEach((comp) => {
      const containerRef = editorAPI.components.getContainer(comp);
      const containerRect = layoutGet(containerRef);
      const compRect = layoutGet(comp);

      const topDistance = compRect.y - containerRect.y;
      if (topDistance < minContainerRect.top) {
        minContainerRect.top = topDistance;
      }

      const bottomDistance =
        containerRect.y + containerRect.height - (compRect.y + compRect.height);
      if (bottomDistance < minContainerRect.bottom) {
        minContainerRect.bottom = bottomDistance;
      }

      const leftDistance = compRect.x - containerRect.x;
      if (leftDistance < minContainerRect.left) {
        minContainerRect.left = leftDistance;
      }

      const rightDistance =
        containerRect.x + containerRect.width - (compRect.x + compRect.width);
      if (rightDistance < minContainerRect.right) {
        minContainerRect.right = rightDistance;
      }
    });

    this.containerRect = {
      top: layout.y - minContainerRect.top,
      bottom: layout.y + layout.height + minContainerRect.bottom,
      left: layout.x - minContainerRect.left,
      right: layout.x + layout.width + minContainerRect.right,
    };
  }

  finalize(_scope: PluginScope): void {
    this.containerRect = null;
  }

  adjustLayout(
    _scope: PluginScope,
    layout: CompLayout,
    options: AdjustLayoutOptions,
  ): CompLayout {
    if (!this.containerRect) {
      throw new Error('containerRect not initialized');
    }

    return adjustLayoutToFit(this.containerRect, layout, options.onCollision);
  }
}

class MobileOnlyLayoutAdjustmentStrategy implements LayoutAdjustmentStrategy {
  initialize(_scope: PluginScope): void {}
  finalize(_scope: PluginScope): void {}
  adjustLayout(_scope: PluginScope, layout: CompLayout): CompLayout {
    return layout;
  }
}

export class LayoutAdjustmentStrategyFactory {
  private scope: PluginScope;

  constructor(scope: PluginScope) {
    this.scope = scope;
  }

  public getStrategy(): LayoutAdjustmentStrategy {
    const { editorAPI, selectedComp, parentContainers } = this.scope;

    if (areAllComponentsMobileOnly(editorAPI, selectedComp)) {
      return new MobileOnlyLayoutAdjustmentStrategy();
    }

    const isMultiselect = selectedComp.length > 1;

    if (isMultiselect) {
      return new MultiselectLayoutAdjustmentStrategy();
    }

    const containerCompType = parentContainers[0].compType;

    switch (containerCompType) {
      case COMP_TYPES.SECTION:
        return new SectionLayoutAdjustmentStrategy();
      default:
        return new DefaultLayoutAdjustmentStrategy();
    }
  }
}
